import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskAPI, userAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { 
  Plus, Filter, Search, Calendar, Clock, User, CheckCircle, 
  AlertCircle, MoreVertical, Edit, Trash2, ChevronDown, X,
  Flag, ListTodo, Users
} from 'lucide-react';
import Pagination from '../components/Pagination';
import { SkeletonList } from '../components/LoadingSkeleton';
import './Tasks.css';

function Tasks() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'board'
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [filters]);

  // Fetch tasks
  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: ['tasks', filters, page, limit],
    queryFn: () => taskAPI.getAll({ ...filters, page, limit }).then(res => res.data),
    keepPreviousData: true,
  });

  // Fetch users for assignment
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => userAPI.getProfile().then(res => res.data),
  });

  // Create task mutation
  const createMutation = useMutation({
    mutationFn: (data) => taskAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      setShowCreateModal(false);
    },
  });

  // Update task mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => taskAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      setSelectedTask(null);
    },
  });

  // Delete task mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => taskAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
    },
  });

  // Complete task mutation
  const completeMutation = useMutation({
    mutationFn: (id) => taskAPI.complete(id, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
    },
  });

  const tasks = tasksData?.tasks || [];

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#f97316',
    urgent: '#ef4444',
  };

  const statusColors = {
    draft: '#6b7280',
    published: '#3b82f6',
    archived: '#9ca3af',
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="tasks-page">
      {/* Header */}
      <div className="tasks-header">
        <div className="header-left">
          <h1><ListTodo size={28} /> Tasks</h1>
          <p className="task-count">{tasksData?.pagination?.total || tasks.length} tasks</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={view === 'list' ? 'active' : ''} 
              onClick={() => setView('list')}
            >
              List
            </button>
            <button 
              className={view === 'board' ? 'active' : ''} 
              onClick={() => setView('board')}
            >
              Board
            </button>
          </div>
          {isAdmin && (
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /> New Task
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="tasks-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
          <Filter size={18} /> Filters <ChevronDown size={16} />
        </button>
        {showFilters && (
          <div className="filter-dropdowns">
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <select 
              value={filters.priority} 
              onChange={(e) => handleFilterChange('priority', e.target.value)}
            >
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <button className="clear-filters" onClick={() => setFilters({ status: '', priority: '', search: '' })}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Task List */}
      {isLoading ? (
        <SkeletonList count={5} lines={3} />
      ) : error ? (
        <div className="error-state">Failed to load tasks. Please try again.</div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <ListTodo size={48} />
          <h3>No tasks found</h3>
          <p>Create your first task to get started.</p>
          {isAdmin && (
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /> Create Task
            </button>
          )}
        </div>
      ) : view === 'list' ? (
        <div className="tasks-list">
          {tasks.map(task => (
            <div key={task.id} className="task-card" onClick={() => setSelectedTask(task)}>
              <div className="task-main">
                <div className="task-checkbox">
                  {task.status === 'archived' ? (
                    <CheckCircle size={20} className="completed" />
                  ) : (
                    <div className="checkbox" onClick={(e) => {
                      e.stopPropagation();
                      if (task.status !== 'archived') {
                        completeMutation.mutate(task.id);
                      }
                    }} />
                  )}
                </div>
                <div className="task-content">
                  <h3 className={task.status === 'archived' ? 'completed' : ''}>{task.title}</h3>
                  {task.description && <p className="task-description">{task.description}</p>}
                  <div className="task-meta">
                    {task.due_date && (
                      <span className="meta-item due-date">
                        <Calendar size={14} />
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {task.assignee_name && (
                      <span className="meta-item assignee">
                        <User size={14} />
                        {task.assignee_name}
                      </span>
                    )}
                    {task.estimated_hours && (
                      <span className="meta-item estimate">
                        <Clock size={14} />
                        {task.estimated_hours}h
                      </span>
                    )}
                    {task.points > 0 && (
                      <span className="meta-item points">
                        +{task.points} pts
                      </span>
                    )}
                  </div>
                </div>
                <div className="task-badges">
                  <span 
                    className="priority-badge" 
                    style={{ backgroundColor: priorityColors[task.priority] }}
                  >
                    <Flag size={12} />
                    {task.priority}
                  </span>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: statusColors[task.status] }}
                  >
                    {task.status}
                  </span>
                </div>
                {isAdmin && (
                  <div className="task-actions">
                    <button onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this task?')) {
                        deleteMutation.mutate(task.id);
                      }
                    }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="tasks-board">
          {['draft', 'published', 'archived'].map(status => (
            <div key={status} className="board-column">
              <h3 className="column-header">
                {status.charAt(0).toUpperCase() + status.slice(1)}
                <span className="count">{tasks.filter(t => t.status === status).length}</span>
              </h3>
              <div className="column-tasks">
                {tasks.filter(t => t.status === status).map(task => (
                  <div key={task.id} className="board-card" onClick={() => setSelectedTask(task)}>
                    <h4>{task.title}</h4>
                    {task.due_date && (
                      <span className="board-due">
                        <Calendar size={12} />
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                    <div className="board-meta">
                      <span 
                        className="priority-dot" 
                        style={{ backgroundColor: priorityColors[task.priority] }}
                      />
                      {task.points > 0 && <span className="points-badge">+{task.points}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {tasksData?.pagination && (
        <Pagination
          page={page}
          totalPages={tasksData.pagination.total_pages}
          total={tasksData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <TaskModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={(data) => updateMutation.mutate({ id: selectedTask.id, data })}
          onDelete={() => deleteMutation.mutate(selectedTask.id)}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

// Task Create/Edit Modal
function TaskModal({ task, onClose, onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    type: task?.type || 'training',
    priority: task?.priority || 'medium',
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    points: task?.points || 0,
    estimated_hours: task?.estimated_hours || '',
    notes: task?.notes || '',
    visibility: task?.visibility || 'team',
    viewers: task?.viewers || [],
  });

  const [showViewerSelect, setShowViewerSelect] = useState(false);

  // Fetch users for viewer selection
  const { data: usersData } = useQuery({
    queryKey: ['users-search', ''],
    queryFn: () => userAPI.searchUsers('').then(res => res.data),
    enabled: formData.visibility === 'custom',
  });

  const availableUsers = usersData?.users || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content task-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task ? 'Edit Task' : 'Create New Task'}</h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add task description..."
              rows={3}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="training">Training</option>
                <option value="social_share">Social Share</option>
                <option value="content_creation">Content Creation</option>
                <option value="engagement">Engagement</option>
                <option value="referral">Referral</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Points</label>
              <input
                type="number"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Visibility</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value, viewers: e.target.value === 'custom' ? formData.viewers : [] })}
            >
              <option value="team">Team (All team members)</option>
              <option value="public">Public (Everyone)</option>
              <option value="private">Private (Only me and assignees)</option>
              <option value="custom">Custom (Select viewers)</option>
            </select>
          </div>
          {formData.visibility === 'custom' && (
            <div className="form-group">
              <label>Select Viewers</label>
              <div className="viewer-selector">
                {availableUsers.map(user => (
                  <label key={user.id} className="viewer-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.viewers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, viewers: [...formData.viewers, user.id] });
                        } else {
                          setFormData({ ...formData, viewers: formData.viewers.filter(id => id !== user.id) });
                        }
                      }}
                    />
                    <span>{user.first_name} {user.last_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Estimated Hours</label>
            <input
              type="number"
              value={formData.estimated_hours}
              onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
              min="0"
              step="0.5"
            />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any additional notes..."
              rows={2}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Task Detail Modal
function TaskDetailModal({ task, onClose, onUpdate, onDelete, isAdmin }) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <TaskModal
        task={task}
        onClose={() => setIsEditing(false)}
        onSubmit={(data) => {
          onUpdate(data);
          setIsEditing(false);
        }}
        isLoading={false}
      />
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content task-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task.title}</h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="task-detail-content">
          {task.description && (
            <div className="detail-section">
              <h4>Description</h4>
              <p>{task.description}</p>
            </div>
          )}
          <div className="detail-grid">
            <div className="detail-item">
              <label>Status</label>
              <span className="status-badge" style={{ backgroundColor: task.status === 'published' ? '#3b82f6' : '#6b7280' }}>
                {task.status}
              </span>
            </div>
            <div className="detail-item">
              <label>Priority</label>
              <span className="priority-badge" style={{ 
                backgroundColor: task.priority === 'urgent' ? '#ef4444' : 
                                task.priority === 'high' ? '#f97316' : 
                                task.priority === 'medium' ? '#f59e0b' : '#10b981' 
              }}>
                {task.priority}
              </span>
            </div>
            <div className="detail-item">
              <label>Type</label>
              <span>{task.type}</span>
            </div>
            <div className="detail-item">
              <label>Points</label>
              <span>+{task.points} pts</span>
            </div>
            {task.due_date && (
              <div className="detail-item">
                <label>Due Date</label>
                <span><Calendar size={14} /> {new Date(task.due_date).toLocaleDateString()}</span>
              </div>
            )}
            {task.assignee_name && (
              <div className="detail-item">
                <label>Assigned To</label>
                <span><User size={14} /> {task.assignee_name}</span>
              </div>
            )}
            {task.creator_name && (
              <div className="detail-item">
                <label>Created By</label>
                <span>{task.creator_name}</span>
              </div>
            )}
            {task.estimated_hours && (
              <div className="detail-item">
                <label>Estimated</label>
                <span><Clock size={14} /> {task.estimated_hours} hours</span>
              </div>
            )}
          </div>
          {task.notes && (
            <div className="detail-section">
              <h4>Notes</h4>
              <p>{task.notes}</p>
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="modal-actions">
            <button className="btn-danger" onClick={() => { if (window.confirm('Are you sure you want to delete this task?')) { onDelete(); onClose(); } }}>
              <Trash2 size={16} /> Delete
            </button>
            <button className="btn-secondary" onClick={() => setIsEditing(true)}>
              <Edit size={16} /> Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Tasks;
